import getConfig from "next/config";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";

import { getUrlUtmObj, GetUrlUtmObjResult } from "helpers/url";
import { getUrlQueries } from "helpers/url";
import { getClientCookie, setClientCookie } from "helpers/cookies";
import { SESSION_UTM_KEY_VENDOR } from "constants/variables";

const { publicRuntimeConfig } = getConfig();

const PATH_YANDEX_VENDOR = "/api/yandex/vendor";

const getYandexMetricaUrl = getUrlQueries({ url: PATH_YANDEX_VENDOR });
const pushYandexMetrica = async (url: string) => {
  try {
    // Делаем запрос в нутри фронта к api у express, для обхода CORS
    await fetch(location.origin + url);
  } catch (err) {
    console.error(err);
  }
};

type YandexMetricaProps = {
  // номер счетчика
  tid: string;
};

export type YmPageviewProps = YandexMetricaProps & {
  // заголовок старницы
  dt: string;
};
export type YmEventProps = YandexMetricaProps & {
  // действия с товаром
  pa: "detail" | "add" | "remove" | "purchase";
  product: Entity.Product | Entity.Product.Short;
  productQuantity?: number;
  // характеристики товара(кол-во, вес и т.д.)
  productVariety?: string;
};

type GetYmFromWindowResult = {
  accurateTrackBounce: () => void;
  addFileExtension: () => void;
  clickmap: () => void;
  destruct: () => void;
  ecommerceAdd: () => void;
  ecommerceDetail: () => void;
  ecommercePurchase: () => void;
  ecommerceRemove: () => void;
  enableAll: () => void;
  experiments: () => void;
  extLink: () => void;
  file: () => void;
  firstPartyParams: () => void;
  firstPartyParamsHashed: () => void;
  getClientID: () => Entity.ID;
  hit: () => void;
  notBounce: () => void;
  params: () => void;
  reachGoal: (goalId: string) => void;
  setUserID: () => void;
  trackHash: () => void;
  trackLinks: () => void;
  userParams: () => void;
};
// Вытягиваем методы от react-yandex-metrika из window
export const getYmFromWindow = (
  delay = 500
): Promise<GetYmFromWindowResult> => {
  const { Y_METRICA } = publicRuntimeConfig;
  let timer;

  const ym = new Promise<GetYmFromWindowResult>(resolve => {
    const getYaCounter = _get(window, `yaCounter${Y_METRICA}`, undefined);
    if (getYaCounter) return resolve(getYaCounter);

    timer = setInterval(() => {
      const value = _get(window, `yaCounter${Y_METRICA}`, undefined);

      if (value) return resolve(value);
    }, delay);
  }).finally(() => timer && clearInterval(timer));

  return ym;
};

// Получение методов яндекс метрики от библиотеки react-yandex-metrika для версия v1
export const yandexMetricaApi = () => ({
  getClientID: async (): Promise<Entity.ID> => {
    const { getClientID } = await getYmFromWindow();
    return getClientID();
  },
  reachGoal: async (goalId: string): Promise<void> => {
    const { reachGoal } = await getYmFromWindow();
    reachGoal(goalId);
  },
});

const isYandexVendorCheckUtms =
  (utms: Array<Common.YandexCounterUtmConfigItem> = []) =>
  (urlUtm: GetUrlUtmObjResult = {}) => {
    if (!utms || utms.length === 0 || !urlUtm || _isEmpty(urlUtm)) return false;
    let isResult = true;

    utms.map(({ key, value }) => {
      const itemKey = urlUtm[key];
      if (!itemKey || (itemKey && itemKey !== value)) isResult = false;
    });

    return isResult;
  };

// Условия по запуску событий вендора
export const runYandexVendorOnUtm = (
  callback: (tid: string) => void = null,
  counterItem: Common.YandexCounterItem = null,
  timeout = 0
) => {
  const utm = getUrlUtmObj(location.href);

  if (utm) {
    setClientCookie(SESSION_UTM_KEY_VENDOR, utm, {
      domain: publicRuntimeConfig.COOKIE_FORWARD_DOMAIN,
    });
  }

  if (counterItem) {
    const getIsCheckUtm = isYandexVendorCheckUtms(counterItem.utmConfig);
    const utmCookie = getClientCookie(
      SESSION_UTM_KEY_VENDOR
    ) as GetUrlUtmObjResult;

    const currentUtm = utmCookie || utm;

    if (currentUtm && getIsCheckUtm(currentUtm)) {
      callback && setTimeout(() => callback(counterItem.value), timeout + 500);
    }
  }
};

type YandexMetricaQuantityCartEventResult = Pick<
  YmEventProps,
  "pa" | "productQuantity"
>;
// Для вычисления события удаления или добавления товара в корзину, для яндекс метрик у вендоров
export const yandexMetricaQuantityCartEvent = (
  prevValue: number,
  nextValue: number
): YandexMetricaQuantityCartEventResult => {
  if (!prevValue) return { pa: "add", productQuantity: nextValue };

  if (prevValue > nextValue) {
    const productQuantity =
      prevValue - nextValue === 1 ? 1 : prevValue - nextValue;

    return { pa: "remove", productQuantity };
  } else if (prevValue < nextValue) {
    const productQuantity =
      nextValue - prevValue === 1 ? 1 : nextValue - prevValue;

    return { pa: "add", productQuantity };
  }
};

export const runVendorCounterPromises = async (
  counters: Entity.Product["counters"] = null,
  callback: (tid: Common.YandexCounterItem) => void
) => {
  if (!counters?.yandex?.items?.length) return;
  await Promise.all(counters.yandex.items.map(item => callback(item)));
};

// Методы апи для яндекс метрики через fetch
export const yandexMetrica = {
  pageview: async ({ tid, dt }: YmPageviewProps) => {
    const { href } = location;
    const dr = document?.referrer;
    const uid = await yandexMetricaApi().getClientID();

    const url = getYandexMetricaUrl({
      t: "pageview",
      uid,
      dl: encodeURIComponent(href),
      tid,
      dt: encodeURIComponent(dt),
      ...(dr && { dr: encodeURIComponent(dr) }),
    });
    await pushYandexMetrica(url);
  },
  event: {
    cartChangeCount: async ({
      product,
      productQuantity,
      productVariety,
      pa,
      tid,
    }: YmEventProps) => {
      const uid = await yandexMetricaApi().getClientID();
      const {
        name = "",
        brand = null,
        category = { path: "" },
        price = "",
        bitrixID = "",
      } = product as Entity.Product;

      const url = getYandexMetricaUrl({
        tid,
        uid,
        t: "event",
        pa,
        ...(bitrixID && { pr1id: bitrixID }),
        ...(name && { pr1nm: name }),
        ...(brand && brand?.name && { pr1br: brand?.name }),
        ...(category.path && { pr1ca: category.path }),
        ...(price && { pr1pr: price }),
        ...(productQuantity && { pr1qt: productQuantity }),
        ...(productVariety && { pr1va: productVariety }),
      });
      await pushYandexMetrica(url);
    },
    purchase: async ({
      tid,
      itemsParams,
    }: {
      tid: string;
      itemsParams: Record<string, string | number>;
    }) => {
      const uid = await yandexMetricaApi().getClientID();

      const url = getYandexMetricaUrl({
        t: "event",
        pa: "purchase",
        uid,
        tid,
        ...(document.referrer && { dr: encodeURIComponent(document.referrer) }),
        ...itemsParams,
      });
      await pushYandexMetrica(url);
    },
  },
};
